import {
  isModulePermission,
  MODULES,
  modulePermission,
  PERMISSIONS,
  type PermissionSourceDto,
  SCOPE_LABELS,
  type Scope,
  scopesFor,
  type UserDto,
  type UserPermissionEffect,
  type UserPermissionEntry,
  type UserPermissionsDto,
} from '@bqcrm/shared';
import { ActionIcon, Alert, Badge, Button, Card, Group, SegmentedControl, Select, Stack, Switch, Table, Text } from '@mantine/core';
import { IconAlertTriangle, IconKey, IconPlus, IconTrash } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { useMe, visiblePermissions } from '../auth';
import { useCompanies, useTrades } from '../data';
import { useSaveShortcut } from '../hooks';
import { notifyError, notifyOk } from '../notify';
import { QueryState } from './QueryState';
import { EmptyState, SectionCard } from './ui';

const key = (e: { permission: string; scope: string }) => `${e.permission}:${e.scope}`;
const LABEL = new Map(PERMISSIONS.map((p) => [p.code, p]));


type ModuleChoice = 'rola' | UserPermissionEffect;

const sourceText = (from: PermissionSourceDto[] | undefined) => (from?.length ? from.map((s) => `rola ${s.label}`).join('; ') : '');

function EffectBadge({ effect }: { effect: UserPermissionEffect }) {
  return effect === 'nadane' ? (
    <Badge size="sm" variant="light" color="teal">
      Nadane
    </Badge>
  ) : (
    <Badge size="sm" variant="light" color="red">
      Odebrane
    </Badge>
  );
}

/**
 * Karta pracownika → „Dostęp” (dolna część): wyjątki — moduły menu i uprawnienia nadane albo odebrane poza rolą
 * (docs/06 §6), to, czy dostaje zadania w sprawach, i wynik „co może i dlaczego”.
 */
export function UserPermissionsPanel({ user, editable, onDirty }: { user: UserDto; editable: boolean; onDirty: (d: boolean) => void }) {
  const qc = useQueryClient();
  const trades = useTrades();
  const companies = useCompanies();
  // moduł Flota wyłączony — jego uprawnienia ukryte (i tak są odbierane w profilu)
  const { data: me } = useMe();
  const perms = visiblePermissions(me);
  const detailGroups = [...new Set(perms.filter((p) => !isModulePermission(p.code)).map((p) => p.group))];
  const q = useQuery({ queryKey: ['user-permissions', user.id], queryFn: () => api<UserPermissionsDto>(`/users/${user.id}/permissions`) });
  const [entries, setEntries] = useState<UserPermissionEntry[]>([]);
  useEffect(() => setEntries(q.data?.entries ?? []), [q.data]);
  const dirty = useMemo(() => {
    const a = (q.data?.entries ?? []).map((e) => `${key(e)}:${e.effect}`).sort().join();
    return a !== entries.map((e) => `${key(e)}:${e.effect}`).sort().join();
  }, [q.data, entries]);
  useEffect(() => onDirty(dirty), [dirty]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => onDirty(false), []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: () => api<UserPermissionsDto>(`/users/${user.id}/permissions`, { method: 'PUT', body: { entries } }),
    onSuccess: (d) => {
      qc.setQueryData(['user-permissions', user.id], d);
      void qc.invalidateQueries({ queryKey: ['me'] });
      notifyOk('Zapisano wyjątki pracownika — obowiązują od razu');
    },
    onError: notifyError,
  });
  useSaveShortcut(editable && dirty && !save.isPending, () => save.mutate());

  const sources = useMemo(() => {
    const m = new Map<string, PermissionSourceDto[]>();
    for (const r of q.data?.sources ?? []) m.set(key(r), r.from);
    return m;
  }, [q.data]);

  const setModule = (code: string, choice: ModuleChoice) =>
    setEntries((list) => {
      const perm = modulePermission(code);
      const rest = list.filter((e) => e.permission !== perm);
      return choice === 'rola' ? rest : [...rest, { permission: perm, scope: 'W', effect: choice }];
    });

  // nowy wpis szczegółowy
  const [perm, setPerm] = useState<string | null>(null);
  const [scope, setScope] = useState<Scope | null>(null);
  const [effect, setEffect] = useState<UserPermissionEffect>('nadane');
  const [showAll, setShowAll] = useState(false);
  const scopes = perm ? scopesFor(perm) : [];
  const permOptions = detailGroups.map((g) => ({
    group: g,
    items: perms.filter((p) => p.group === g).map((p) => ({ value: p.code, label: `${p.label} (${p.code})` })),
  }));
  const duplicate = !!perm && !!scope && entries.some((e) => key(e) === `${perm}:${scope}`);
  const add = () => {
    if (!perm || !scope) return;
    setEntries((l) => [...l, { permission: perm, scope, effect }]);
    setPerm(null);
    setScope(null);
  };

  const details = entries.filter((e) => !isModulePermission(e.permission)).sort((a, b) => a.permission.localeCompare(b.permission) || a.scope.localeCompare(b.scope));
  const effective = q.data?.effective ?? {};
  const tradeName = (c: string | null) => (c === null ? 'wszystkie branże' : (trades.data?.find((t) => t.code === c)?.name ?? c));
  const companyName = (id: string) => companies.data?.find((c) => c.id === id)?.shortName ?? id;
  // zakres po zawężeniu: F — firmy pracownika (z nazwami)
  const scopeText = (s: Scope) => (s === 'F' ? `firmy pracownika (${(q.data?.companyIds ?? []).map(companyName).join(', ') || '—'})` : `${s} — ${SCOPE_LABELS[s]}`);
  // źródło zakresu F — zakres W z roli przed zawężeniem
  const sourceOf = (permission: string, s: Scope) => sources.get(`${permission}:${s === 'F' ? 'W' : s}`);

  return (
    <QueryState isLoading={q.isLoading} error={q.error}>
      <Stack>
        {(q.data?.warnings ?? []).map((w) => (
          <Alert key={w} color="orange" variant="light" icon={<IconAlertTriangle size={16} />}>
            {w}
          </Alert>
        ))}

        <SectionCard title="Zadania w sprawach — czy trafiają do pracownika" padding={0}>
          <Table.ScrollContainer minWidth={620}>
            <Table verticalSpacing={6} highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Zadanie</Table.Th>
                  <Table.Th w={110}>Trafia</Table.Th>
                  <Table.Th>Dlaczego</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(q.data?.tasks ?? []).map((t) => (
                  <Table.Tr key={t.type}>
                    <Table.Td>
                      <Text size="sm">{t.name}</Text>
                      <Text size="xs" c="dimmed">
                        {t.permission}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {t.ok ? (
                        <Badge size="sm" variant="light" color="teal">
                          Tak
                        </Badge>
                      ) : (
                        <Badge size="sm" variant="light" color="gray">
                          Nie
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c={t.ok ? 'dimmed' : undefined}>
                        {t.ok ? `branże: ${t.functionTrades.map(tradeName).join(', ')}${t.companyIds ? `; firmy: ${t.companyIds.map(companyName).join(', ')}` : ''}` : t.reasons.join('; ')}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </SectionCard>

        <SectionCard title="Moduły (pozycje lewego menu)" padding={0}>
          <Table.ScrollContainer minWidth={620}>
            <Table verticalSpacing={6} highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Moduł</Table.Th>
                  <Table.Th>Z ról</Table.Th>
                  <Table.Th w={250}>Ustawienie</Table.Th>
                  <Table.Th w={110}>Wynik</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {MODULES.filter((m) => perms.some((p) => p.code === modulePermission(m.code))).map((m) => {
                  const p = modulePermission(m.code);
                  const from = sources.get(`${p}:W`);
                  const own = entries.find((e) => e.permission === p);
                  const choice: ModuleChoice = own?.effect ?? 'rola';
                  const visible = choice === 'nadane' || (choice === 'rola' && !!from?.length);
                  return (
                    <Table.Tr key={m.code}>
                      <Table.Td>
                        <Text size="sm">{m.label}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c={from?.length ? undefined : 'dimmed'}>
                          {sourceText(from) || '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <SegmentedControl
                          size="xs"
                          disabled={!editable}
                          value={choice}
                          onChange={(v) => setModule(m.code, v as ModuleChoice)}
                          data={[
                            { value: 'rola', label: 'Jak w roli' },
                            { value: 'nadane', label: 'Nadaj' },
                            { value: 'odebrane', label: 'Odbierz' },
                          ]}
                        />
                      </Table.Td>
                      <Table.Td>
                        {visible ? (
                          <Badge size="sm" variant="light" color="teal">
                            Widzi
                          </Badge>
                        ) : (
                          <Badge size="sm" variant="light" color="gray">
                            Nie widzi
                          </Badge>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </SectionCard>

        <SectionCard title="Wyjątki — uprawnienia nadane lub odebrane tylko temu pracownikowi" padding={0}>
          <Table.ScrollContainer minWidth={620}>
            <Table verticalSpacing={6} highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Czynność</Table.Th>
                  <Table.Th w={130}>Zakres</Table.Th>
                  <Table.Th w={110}>Zmiana</Table.Th>
                  <Table.Th>Z ról</Table.Th>
                  {editable && <Table.Th w={40} />}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {details.map((e) => (
                  <Table.Tr key={key(e)}>
                    <Table.Td>
                      <Text size="sm">{LABEL.get(e.permission)?.label ?? e.permission}</Text>
                      <Text size="xs" c="dimmed">
                        {e.permission}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {e.scope} — {SCOPE_LABELS[e.scope]}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <EffectBadge effect={e.effect} />
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {sourceText(sources.get(key(e))) || '—'}
                      </Text>
                    </Table.Td>
                    {editable && (
                      <Table.Td>
                        <ActionIcon variant="subtle" color="red" aria-label="Usuń wpis" onClick={() => setEntries((l) => l.filter((x) => key(x) !== key(e)))}>
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Table.Td>
                    )}
                  </Table.Tr>
                ))}
                {details.length === 0 && (
                  <Table.Tr>
                    <Table.Td colSpan={editable ? 5 : 4}>
                      <EmptyState compact icon={<IconKey size={18} />} title="Brak wyjątków" hint={editable ? 'Dodaj uprawnienie poniżej, gdy pracownik potrzebuje czegoś więcej albo mniej niż daje rola.' : 'Pracownik ma dokładnie uprawnienia swoich ról.'} />
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          {editable && (
            <Group align="flex-end" wrap="wrap" p="md" gap="sm" style={{ borderTop: '1px solid var(--app-border)' }}>
              <Select
                label="Czynność"
                placeholder="Wyszukaj uprawnienie"
                searchable
                data={permOptions}
                value={perm}
                onChange={(v) => {
                  setPerm(v);
                  setScope(v ? (scopesFor(v).length === 1 ? scopesFor(v)[0]! : null) : null);
                }}
                w={{ base: '100%', sm: 360 }}
              />
              <Select label="Zakres" data={scopes.map((s) => ({ value: s, label: `${s} — ${SCOPE_LABELS[s]}` }))} value={scope} onChange={(v) => setScope(v as Scope | null)} disabled={!perm} w={{ base: '100%', sm: 160 }} />
              <SegmentedControl
                size="sm"
                value={effect}
                onChange={(v) => setEffect(v as UserPermissionEffect)}
                data={[
                  { value: 'nadane', label: 'Nadaj' },
                  { value: 'odebrane', label: 'Odbierz' },
                ]}
              />
              <Button size="xs" leftSection={<IconPlus size={14} />} disabled={!perm || !scope || duplicate} onClick={add}>
                Dodaj uprawnienie
              </Button>
              {duplicate && (
                <Text size="xs" c="var(--app-text-orange)">
                  To uprawnienie w tym zakresie jest już na liście
                </Text>
              )}
            </Group>
          )}
        </SectionCard>

        <Card padding="md">
          <Group justify="space-between" wrap="wrap">
            <Text size="sm">
              Uprawnienia po zmianach (zapisane): <b>{Object.keys(effective).filter((p) => !isModulePermission(p)).length}</b> czynności,{' '}
              <b>{Object.keys(effective).filter(isModulePermission).length}</b> modułów
            </Text>
            <Switch size="sm" label="Co może i dlaczego — pokaż wszystkie uprawnienia i ich źródło" checked={showAll} onChange={(e) => setShowAll(e.currentTarget.checked)} />
          </Group>
          {showAll && (
            <Table.ScrollContainer minWidth={560} mt="sm">
              <Table verticalSpacing={4}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Czynność</Table.Th>
                    <Table.Th w={120}>Zakres</Table.Th>
                    <Table.Th>Skąd</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {perms.filter((p) => !isModulePermission(p.code) && effective[p.code]).flatMap((p) =>
                    effective[p.code]!.map((s) => (
                      <Table.Tr key={`${p.code}:${s}`}>
                        <Table.Td>
                          <Text size="sm">{p.label}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{scopeText(s)}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs" c="dimmed">
                            {sourceText(sourceOf(p.code, s)) || 'wyjątek: nadane'}
                            {s === 'F' ? ' · zawężone do firm' : ''}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    )),
                  )}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </Card>

        {editable && (
          <Group justify="flex-end" pos="sticky" bottom={0} py="sm" style={{ background: 'var(--app-bg)', borderTop: '1px solid var(--app-border)' }}>
            {dirty && (
              <Text size="sm" c="var(--app-text-orange)" fw={500} mr="auto">
                Niezapisane zmiany
              </Text>
            )}
            <Button variant="default" disabled={!dirty} onClick={() => setEntries(q.data?.entries ?? [])}>
              Przywróć
            </Button>
            <Button disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>
              Zapisz wyjątki
            </Button>
          </Group>
        )}
      </Stack>
    </QueryState>
  );
}
